import { g, Tool } from '@hcie/core';
import { buildLassoSelection, buildPolygonalSelection, isPointInSelection, simplifyPath } from '@hcie/tools';
import { SelectionMode } from '@hcie/shared';

export class PolySelectionHandler {
    static handleMouseDown(e: MouseEvent): boolean {
        const id = g.current_tool.id;
        if (id !== Tool.Lasso.id && id !== Tool.PolySelect.id) return false;

        if (g.selectionCanvas) {
            const bc = document.createElement('canvas');
            bc.width = g.image_width; bc.height = g.image_height;
            const bctx = bc.getContext('2d');
            if (bctx) bctx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
            (g as any)._selectionBaseCanvas = bc;
        }

        const handleSize = 10 / g.zoom;
        const pts = (id === Tool.PolySelect.id) ? ((window as any).polyPoints || []) : ((window as any).lassoPoints || []);
        
        let hitIdx = -1;
        for (let i = 0; i < pts.length; i++) {
            if (Math.abs(g.pX - pts[i].x) < handleSize && Math.abs(g.pY - pts[i].y) < handleSize) {
                hitIdx = i; break;
            }
        }

        if (hitIdx !== -1) {
            (g as any)._draggingPointIndex = hitIdx;
            (g as any)._draggingPointSource = id === Tool.PolySelect.id ? 'poly' : 'lasso';
            g.drawing = true;
            return true;
        }

        if (g.isSelectionActive && typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            g.movingSelection = true;
            g.startX = g.pX; g.startY = g.pY;
            return true;
        }

        if (id === Tool.Lasso.id) {
            (window as any).lassoPoints = [{ x: g.pX, y: g.pY }];
            g.drawing = true;
        } else if (id === Tool.PolySelect.id) {
            if (!(window as any).polyPoints) (window as any).polyPoints = [];
            (window as any).polyPoints.push({ x: g.pX, y: g.pY });
            g.drawing = true;
        }
        return true;
    }

    static handleMouseMove(e: MouseEvent): boolean {
        const id = g.current_tool.id;
        if (id !== Tool.Lasso.id && id !== Tool.PolySelect.id) return false;
        if (!g.drawing && !g.movingSelection) return false;

        const mode = ((g as any)._lastSelectionMode || (e.shiftKey ? "add" : (e.altKey ? "subtract" : "replace"))) as SelectionMode;

        if (g.movingSelection) {
            const dx = Math.round(g.pX - g.startX);
            const dy = Math.round(g.pY - g.startY);
            if (typeof (window as any).moveSelectionMask === 'function') {
                (window as any).moveSelectionMask(dx, dy);
                const pts = (id === Tool.PolySelect.id) ? (window as any).polyPoints : (window as any).lassoPoints;
                if (pts) pts.forEach((p: any) => { p.x += dx; p.y += dy; });
            }
            g.startX = g.pX; g.startY = g.pY;
            return true;
        }

        const dragIdx = (g as any)._draggingPointIndex;
        if (dragIdx !== -1) {
            const pts = (g as any)._draggingPointSource === 'poly' ? (window as any).polyPoints : (window as any).lassoPoints;
            if (pts && pts[dragIdx]) {
                pts[dragIdx].x = g.pX; pts[dragIdx].y = g.pY;
                if (id === Tool.PolySelect.id) buildPolygonalSelection(pts, mode);
                else buildLassoSelection(pts, mode);
            }
            return true;
        }

        if (id === Tool.Lasso.id && g.drawing) {
            (window as any).lassoPoints.push({ x: g.pX, y: g.pY });
            buildLassoSelection((window as any).lassoPoints, mode);
            return true;
        }
        return false;
    }

    static handleMouseUp(e: MouseEvent): boolean {
        const id = g.current_tool.id;
        if (id !== Tool.Lasso.id && id !== Tool.PolySelect.id) return false;
        if (!g.drawing && !g.movingSelection) return false;
        
        console.log(`[DEBUG 2026-04-07][TaskID: PolySelect] MouseUp tool=${id}`);

        const mode = ((g as any)._lastSelectionMode || (e.shiftKey ? "add" : (e.altKey ? "subtract" : "replace"))) as SelectionMode;

        if (g.movingSelection) {
            g.movingSelection = false;
        } else if ((g as any)._draggingPointIndex !== -1) {
            (g as any)._draggingPointIndex = -1;
            g.drawing = false;
        } else if (id === Tool.Lasso.id) {
            let pts = (window as any).lassoPoints || [];
            const tolerance = (g as any).settings?.selection?.lassoSmoothing || 0;
            if (tolerance > 0.1 && typeof simplifyPath === 'function') pts = simplifyPath(pts, tolerance);
            
            buildLassoSelection(pts, mode);
            g.isSelectionActive = true;
            if ((g as any).settings?.selection?.ghostMode) {
                (window as any).lassoPoints = pts;
                (g as any)._lastSelectionMode = mode;
            } else {
                g.drawing = false;
                (window as any).lassoPoints = [];
            }
        }
        return true;
    }

    static handleDoubleClick(e: MouseEvent): boolean {
        // ... (existing code)
        const id = g.current_tool.id;
        if (id !== Tool.PolySelect.id) return false;
        const pts = (window as any).polyPoints;
        if (!pts || pts.length < 3) return false;

        // Cleanup redundant points from dblclick
        while (pts.length > 2 && pts[pts.length - 1].x === pts[pts.length - 2].x && pts[pts.length - 1].y === pts[pts.length - 2].y) pts.pop();

        const mode = ((g as any)._lastSelectionMode || (e.shiftKey ? "add" : (e.altKey ? "subtract" : "replace"))) as SelectionMode;
        buildPolygonalSelection(pts, mode);
        
        g.isSelectionActive = true;
        if (!(g as any).settings?.selection?.ghostMode) {
            (window as any).polyPoints = [];
            g.drawing = false;
        } else {
            (g as any)._lastSelectionMode = mode;
        }
        return true;
    }

    static getHoverCursor(): string | null {
        const id = g.current_tool.id;
        const pts = (id === Tool.PolySelect.id) ? ((window as any).polyPoints || []) : ((window as any).lassoPoints || []);
        const s = 10 / g.zoom;

        for (let i = 0; i < pts.length; i++) {
            if (Math.abs(g.pX - pts[i].x) < s && Math.abs(g.pY - pts[i].y) < s) {
                return 'pointer';
            }
        }

        if (g.isSelectionActive && typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            return 'move';
        }

        return null;
    }
}
